// ==========================
// FILE: src/components/Ewill.js
// ==========================
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { db, storage } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
} from "firebase/storage";
import { FiEye, FiRefreshCw, FiUploadCloud } from "react-icons/fi";
import { FaGavel, FaYoutube } from "react-icons/fa";
import PageScaffold from "./ui/PageScaffold";
import VideoLightbox from "./ui/VideoLightbox";
import { useAccess } from "../access/AccessContext";
import "../styles/layout.css";
import { sanitizeRichText } from "../utils/sanitizeHtml";

/* Keeps all your original functionality; notable tweaks:
   - `uploadOne` wrapped in useCallback to satisfy react-hooks/exhaustive-deps.
*/

const COUNTRIES = [
  "Ireland",
  "United Kingdom",
  "United States",
  "Canada",
  "Australia",
  "New Zealand",
  "Other",
];

const COVER_LOGO_SRC = "/Lastwishbox-logo.png";
const SOLICITOR_FINDER_URL = "https://directory.lastwishbox.com/home-solicitor/";

function htmlToText(html) {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return (div.textContent || div.innerText || "").trim();
}

export default function Ewill({ user }) {
  const { readOnly } = useAccess() || { readOnly: false };

  // ---- doc state ----
  const [content, setContent] = useState("");
  const [files, setFiles] = useState([]); // [{name,url,time}]
  const [trusted, setTrusted] = useState([]); // [email]
  const [versions, setVersions] = useState([]); // [{updatedISO, snapshot:{...}}]

  // ---- signing / meta ----
  const [signatureName, setSignatureName] = useState("");
  const [signatureDate, setSignatureDate] = useState("");
  const [witness1Name, setWitness1Name] = useState("");
  const [witness1Email, setWitness1Email] = useState("");
  const [witness2Name, setWitness2Name] = useState("");
  const [witness2Email, setWitness2Email] = useState("");
  const [locationOriginal, setLocationOriginal] = useState("");
  const [executorNotified, setExecutorNotified] = useState(false);
  const [jurisdiction, setJurisdiction] = useState("Ireland");

  // ---- ui state ----
  const [mode, setMode] = useState("choose"); // "choose" | "create" | "upload"
  const [persistedPref, setPersistedPref] = useState(null); // last saved pref if any
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [newTrusted, setNewTrusted] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [drawerMode, setDrawerMode] = useState("how"); // "how" | "solicitor"
  const [showVideo, setShowVideo] = useState(false);
  const [viewVersion, setViewVersion] = useState(null);
  const [includeCover, setIncludeCover] = useState(true);
  const [solicitorIframeKey, setSolicitorIframeKey] = useState(0);
  const [solicitorLoading, setSolicitorLoading] = useState(false);

  const fileInput = useRef(null);
  const printRootRef = useRef(null);
  const solicitorIframeRef = useRef(null);

  // helpers to persist preference
  const prefKey = user?.uid ? `ewill_pref_${user.uid}` : null;
  const saveLocalPref = (m) => { try { if (prefKey) localStorage.setItem(prefKey, m); } catch {} };
  const readLocalPref = () => { try { return prefKey ? localStorage.getItem(prefKey) : null; } catch { return null; } };
  const clearLocalPref = () => { try { if (prefKey) localStorage.removeItem(prefKey); } catch {} };

  function makeSnapshot(state) {
    return {
      content: state.content,
      files: state.files,
      trusted: state.trusted,
      signatureName: state.signatureName,
      signatureDate: state.signatureDate,
      witness1Name: state.witness1Name,
      witness1Email: state.witness1Email,
      witness2Name: state.witness2Name,
      witness2Email: state.witness2Email,
      locationOriginal: state.locationOriginal,
      executorNotified: state.executorNotified,
      jurisdiction: state.jurisdiction,
    };
  }
  function applySnapshot(s) {
    setContent(s.content || "");
    setFiles(Array.isArray(s.files) ? s.files : []);
    setTrusted(Array.isArray(s.trusted) ? s.trusted : []);
    setSignatureName(s.signatureName || "");
    setSignatureDate(s.signatureDate || "");
    setWitness1Name(s.witness1Name || "");
    setWitness1Email(s.witness1Email || "");
    setWitness2Name(s.witness2Name || "");
    setWitness2Email(s.witness2Email || "");
    setLocationOriginal(s.locationOriginal || "");
    setExecutorNotified(!!s.executorNotified);
    setJurisdiction(s.jurisdiction || "Ireland");
  }
  function normalizeVersion(v) {
    if (v.snapshot) return v;
    return { updatedISO: v.updatedISO, snapshot: { content: v.content || "" } };
  }

  // ---- load document ----
  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      const ref = doc(db, "ewills", user.uid);
      const snap = await getDoc(ref);
      let nextMode = "choose";
      let docPref = null;

      if (snap.exists()) {
        const d = snap.data();
        setContent(d.content || "");
        setFiles(Array.isArray(d.files) ? d.files : []);
        setTrusted(Array.isArray(d.trusted) ? d.trusted : []);
        setVersions(Array.isArray(d.versions) ? d.versions.map(normalizeVersion) : []);
        setSignatureName(d.signatureName || "");
        setSignatureDate(d.signatureDate || "");
        setWitness1Name(d.witness1Name || "");
        setWitness1Email(d.witness1Email || "");
        setWitness2Name(d.witness2Name || "");
        setWitness2Email(d.witness2Email || "");
        setLocationOriginal(d.locationOriginal || "");
        setExecutorNotified(!!d.executorNotified);
        setJurisdiction(d.jurisdiction || "Ireland");
        setLastSaved(d.updated?.toDate ? d.updated.toDate() : null);
        docPref = d.willMode || null;
      }

      const hasExisting =
        (content && content.trim().length > 0) ||
        (files && files.length > 0) ||
        (snap.exists() && ((snap.data()?.content || "").trim().length > 0 || (snap.data()?.files || []).length > 0));

      const localPref = readLocalPref();
      const pref = docPref || localPref;

      if (pref === "create" || pref === "upload") {
        nextMode = pref;
      } else if (hasExisting) {
        nextMode = "create"; // legacy default if data exists but no recorded pref
      } else {
        nextMode = "choose";
      }

      setPersistedPref(pref || (hasExisting ? "create" : null));
      setMode(nextMode);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const disclaimerText = useMemo(() => {
    switch (jurisdiction) {
      case "Ireland":
        return "In Ireland, a will generally requires a signature and two independent witnesses. An electronic will may not satisfy statutory formalities; consult a solicitor.";
      case "United Kingdom":
        return "Under UK law, wills typically require a signature and two witnesses in person. Electronic forms may not be valid; seek legal advice.";
      case "United States":
        return "US states vary. Many require a signed, witnessed paper will. Some recognise e-wills with specific rules. Check your state’s requirements.";
      case "Canada":
        return "Canadian provinces/territories differ; most require a signed, witnessed paper will. Verify local e-will laws.";
      case "Australia":
        return "Australian states/territories have differing rules; consult a lawyer about electronic execution and witnessing.";
      case "New Zealand":
        return "New Zealand generally requires a signed will with two witnesses. Electronic execution may not be sufficient; get legal advice.";
      default:
        return "Many jurisdictions require a signed, witnessed paper will. Please consult local legal counsel regarding electronic wills.";
    }
  }, [jurisdiction]);

  const resetSolicitorSearch = useCallback(() => {
    setSolicitorLoading(true);
    setSolicitorIframeKey((k) => k + 1);
  }, []);

  const startNewSolicitorSearch = useCallback(() => {
    resetSolicitorSearch();
  }, [resetSolicitorSearch]);

  const drawerBodyContent = (
    <div className="ew-drawer-iframe-wrap">
      {solicitorLoading && (
        <div className="ew-directory-loading" aria-live="polite" aria-label="Directory loading">
          <div className="ew-directory-loading__text">Directory loading, please wait…</div>
        </div>
      )}
      <iframe
        key={solicitorIframeKey}
        ref={solicitorIframeRef}
        title="Find a local solicitor"
        src={SOLICITOR_FINDER_URL}
        className="ew-drawer-iframe"
        onLoad={() => setSolicitorLoading(false)}
        loading="lazy"
        sandbox="allow-forms allow-same-origin allow-scripts"
      />
      <div className="ew-drawer-iframe-footer">
        <button
          type="button"
          className="btn ew-solicitor-new-search"
          onClick={startNewSolicitorSearch}
          style={{ background: "#f97316", borderColor: "#f97316", color: "#fff" }}
        >
          <FiRefreshCw aria-hidden className="ew-solicitor-return__icon" />
          <span>Start a New Search</span>
        </button>
      </div>
    </div>
  );

  const solicitorDrawerButton = (
    <button
      className="btn btn-main ew-choose__card ew-solicitor-btn"
      type="button"
      onClick={() => { setSolicitorLoading(true); setDrawerMode("solicitor"); setShowDrawer(true); }}
    >
      <FaGavel className="ew-solicitor-btn__icon" aria-hidden />
      <span>Find A Local Solicitor</span>
    </button>
  );

  // ---- save (owner) ----
  async function handleSave() {
    if (!user?.uid) return;
    setSaving(true);
    try {
      const ref = doc(db, "ewills", user.uid);
      const snap = await getDoc(ref);

      let nextVersions = Array.isArray(versions) ? versions.map(normalizeVersion) : [];
      if (snap.exists()) {
        const d = snap.data();
        const prev = {
          content: d.content || "",
          files: Array.isArray(d.files) ? d.files : [],
          trusted: Array.isArray(d.trusted) ? d.trusted : [],
          signatureName: d.signatureName || "",
          signatureDate: d.signatureDate || "",
          witness1Name: d.witness1Name || "",
          witness1Email: d.witness1Email || "",
          witness2Name: d.witness2Name || "",
          witness2Email: d.witness2Email || "",
          locationOriginal: d.locationOriginal || "",
          executorNotified: !!d.executorNotified,
          jurisdiction: d.jurisdiction || "Ireland",
        };
        const curr = makeSnapshot({
          content,
          files,
          trusted,
          signatureName,
          signatureDate,
          witness1Name,
          witness1Email,
          witness2Name,
          witness2Email,
          locationOriginal,
          executorNotified,
          jurisdiction,
        });
        const changed = JSON.stringify(prev) !== JSON.stringify(curr);
        if (changed && (prev.content.trim() || JSON.stringify(prev) !== JSON.stringify({ content: "" }))) {
          nextVersions = [...nextVersions, { updatedISO: new Date().toISOString(), snapshot: prev }];
        }
      }

      await setDoc(
        ref,
        {
          uid: user.uid,
          willMode: mode, // record preference
          content,
          files,
          trusted,
          signatureName,
          signatureDate,
          witness1Name,
          witness1Email,
          witness2Name,
          witness2Email,
          locationOriginal,
          executorNotified,
          jurisdiction,
          versions: nextVersions,
          updated: serverTimestamp(),
        },
        { merge: true }
      );

      saveLocalPref(mode);
      setPersistedPref(mode);
      setVersions(nextVersions);
      setLastSaved(new Date());
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 3000); // 3 seconds
    } catch (err) {
      alert("Save failed: " + (err?.message || String(err)));
    }
    setSaving(false);
  }

  // ---- upload helpers (multi-file) ----
  const uploadOne = useCallback(
    async (f) => {
      const uid = user?.uid;
      if (!uid) return;
      const path = `ewill/${uid}/${Date.now()}_${f.name}`;
      const sref = storageRef(storage, path);
      const task = uploadBytesResumable(sref, f);
      await new Promise((res, rej) => task.on("state_changed", null, rej, res));
      const url = await getDownloadURL(task.snapshot.ref);
      const newFile = { name: f.name, url, time: Date.now() };
      const ref = doc(db, "ewills", uid);
      await setDoc(ref, {}, { merge: true });
      await updateDoc(ref, { files: arrayUnion(newFile) });
      setFiles((prev) => [...prev, newFile]);
    },
    [user?.uid] // stable dep so eslint is happy
  );

  const handleFileInputMulti = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length || !user?.uid) return;
    setFileUploading(true);
    try {
      for (const f of list) await uploadOne(f);
    } catch (err) {
      alert("Upload failed: " + (err?.message || String(err)));
    }
    setFileUploading(false);
    if (fileInput.current) fileInput.current.value = "";
  };

  const onDrop = useCallback(
    async (ev) => {
      ev.preventDefault();
      if (!user?.uid) return;
      const list = Array.from(ev.dataTransfer?.files || []);
      if (!list.length) return;
      setFileUploading(true);
      try {
        for (const f of list) await uploadOne(f);
      } catch (err) {
        alert("Upload failed: " + (err?.message || String(err)));
      }
      setFileUploading(false);
    },
    [user?.uid, uploadOne] // uploadOne now memoized
  );
  const onDragOver = useCallback((ev) => ev.preventDefault(), []);

  async function handleDeleteFile(f) {
    if (!user?.uid) return;
    if (!window.confirm(`Remove “${f.name}”?`)) return;
    try {
      const enc = f.url.split("/o/")[1]?.split("?")[0] || "";
      if (enc) await deleteObject(storageRef(storage, decodeURIComponent(enc)));
    } catch {}
    try {
      const ref = doc(db, "ewills", user.uid);
      await updateDoc(ref, { files: arrayRemove(f) });
      setFiles((prev) => prev.filter((x) => x.url !== f.url));
    } catch (err) {
      alert("Failed to update file list: " + (err?.message || String(err)));
    }
  }

  // ---- change preferences (wipe everything) ----
  async function handleChangePreferences() {
    if (!user?.uid) return;
    const warn =
      "If you change your will preferences, ALL previously saved will data (text, files, versions) will be permanently deleted. Continue?";
    if (!window.confirm(warn)) return;

    // delete all known files
    try {
      for (const f of files) {
        const enc = f.url.split("/o/")[1]?.split("?")[0] || "";
        if (enc) {
          try { await deleteObject(storageRef(storage, decodeURIComponent(enc))); } catch {}
        }
      }
      // best effort: delete any other files in ewill/{uid}
      try {
        const folderRef = storageRef(storage, `ewill/${user.uid}`);
        const listing = await listAll(folderRef);
        for (const item of listing.items) {
          try { await deleteObject(item); } catch {}
        }
      } catch {}
    } catch {}

    // delete firestore doc
    try {
      await deleteDoc(doc(db, "ewills", user.uid));
    } catch {}

    // local reset
    applySnapshot({
      content: "",
      files: [],
      trusted: [],
      signatureName: "",
      signatureDate: "",
      witness1Name: "",
      witness1Email: "",
      witness2Name: "",
      witness2Email: "",
      locationOriginal: "",
      executorNotified: false,
      jurisdiction: "Ireland",
    });
    setVersions([]);
    clearLocalPref();
    setPersistedPref(null);
    setMode("choose");
  }

  async function loadScript(src) {
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = () => reject(new Error("Failed to load " + src));
      document.head.appendChild(s);
    });
  }
  async function ensurePdfLibs() {
    if (!window.html2canvas) {
      await loadScript("https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js");
    }
    if (!window.jspdf && !(window.jsPDF && window.jsPDF.jsPDF)) {
      await loadScript("https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js");
    }
    const jsPDF = window.jspdf?.jsPDF || window.jsPDF?.jsPDF;
    if (!jsPDF) throw new Error("jsPDF not available");
    return { jsPDF };
  }

  function printFull() {
    const root = printRootRef.current;
    if (!root) return;
    const prev = {
      position: root.style.position,
      left: root.style.left,
      top: root.style.top,
      opacity: root.style.opacity,
      display: root.style.display,
    };
    root.style.position = "static";
    root.style.left = "auto";
    root.style.top = "auto";
    root.style.opacity = 1;
    root.style.display = "block";
    root.classList.add("print-root");
    const after = () => {
      root.classList.remove("print-root");
      root.style.position = prev.position;
      root.style.left = prev.left;
      root.style.top = prev.top;
      root.style.opacity = prev.opacity;
      root.style.display = prev.display;
      window.removeEventListener("afterprint", after);
    };
    window.addEventListener("afterprint", after);
    requestAnimationFrame(() => window.print());
  }
  async function downloadPDF() {
    try {
      const { jsPDF } = await ensurePdfLibs();
      const root = printRootRef.current;
      if (!root) return;

      const prev = {
        position: root.style.position,
        left: root.style.left,
        top: root.style.top,
        opacity: root.style.opacity,
        display: root.style.display,
      };
      root.style.position = "static";
      root.style.left = "auto";
      root.style.top = "auto";
      root.style.opacity = 1;
      root.style.display = "block";

      const docPdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
      await docPdf.html(root, {
        margin: 24,
        autoPaging: "text",
        html2canvas: { scale: 0.95, useCORS: true, backgroundColor: "#ffffff", letterRendering: true },
        windowWidth: 780,
      });
      docPdf.save("e-will.pdf");

      root.style.position = prev.position;
      root.style.left = prev.left;
      root.style.top = prev.top;
      root.style.opacity = prev.opacity;
      root.style.display = prev.display;
    } catch (err) {
      alert("PDF export failed. You can still use Print → Save as PDF.\n" + (err?.message || String(err)));
    }
  }

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link"],
        ["clean"],
      ],
    }),
    []
  );

  // ==== READ-ONLY VIEW ====
  if (readOnly) {
    const summaryText = [
      `Signatory: ${signatureName || "-"}`,
      `Signed on: ${signatureDate || "-"}`,
      `Jurisdiction: ${jurisdiction || "-"}`,
      `Location of Original: ${locationOriginal || "-"}`,
      `Executors informed: ${executorNotified ? "Yes" : "No"}`,
      `Witness 1: ${witness1Name || "-"}`,
      `Witness 1 Email: ${witness1Email || "-"}`,
      `Witness 2: ${witness2Name || "-"}`,
      `Witness 2 Email: ${witness2Email || "-"}`,
      "",
      "Trusted Contacts / Executors:",
      trusted?.length ? trusted.join("; ") : "(none listed)",
      "",
      "Supporting Documents:",
      files?.length ? files.map((f) => `${f.name}`).join("; ") : "(none attached)",
      "",
      "Will Text:",
      htmlToText(content) || "(empty)",
    ].join("\n");

    return (
      <div className="ew-root">
        {/* disable global FAB */}
        <PageScaffold
          title="E-will (Secure Electronic Will)"
          heroSrc="/lastwish-account-ewill.jpg"
          heroAlt="E-will"
          onAdd={undefined}
          afterHero={solicitorDrawerButton}
          actionsDesktop={
            <div className="dp-help-row">
              <button className="btn dp-help read" onClick={() => { setDrawerMode("how"); setShowDrawer(true); }} data-allow-readonly>
                <FiEye className="dp-help-ico" /> Read how it works
              </button>
              <button className="btn dp-help see" onClick={() => setShowVideo(true)} data-allow-readonly>
                <FaYoutube className="dp-help-ico" /> See how it works
              </button>
            </div>
          }
          actionsMobile={
            <div className="dp-help-row">
              <button className="btn dp-help read" onClick={() => { setDrawerMode("how"); setShowDrawer(true); }} data-allow-readonly>
                <FiEye className="dp-help-ico" /> Read how it works
              </button>
              <button className="btn dp-help see" onClick={() => setShowVideo(true)} data-allow-readonly>
                <FaYoutube className="dp-help-ico" /> See how it works
              </button>
            </div>
          }
          description={<div className="dp-intro">View the owner’s E-will. You can download, export, or forward a summary.</div>}
        >
          <div className="ew-card">
            <div className="ew-doc">
              <EwillDocument
                includeCover={true}
                user={user}
                content={content}
                files={files}
                trusted={trusted}
                signatureName={signatureName}
                signatureDate={signatureDate}
                witness1Name={witness1Name}
                witness1Email={witness1Email}
                witness2Name={witness2Name}
                witness2Email={witness2Email}
                locationOriginal={locationOriginal}
                executorNotified={executorNotified}
                jurisdiction={jurisdiction}
                disclaimerText={disclaimerText}
                lastSaved={lastSaved}
              />
            </div>

            <div className="form-actions" style={{ marginTop: 12 }}>
              <button className="btn btn-main" type="button" onClick={downloadPDF} data-allow-readonly>
                Download
              </button>
              <button
                className="btn btn-main"
                type="button"
                onClick={() => {
                  const subject = "E-will summary";
                  const body = encodeURIComponent(summaryText);
                  window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${body}`;
                }}
                data-allow-readonly
              >
                Forward
              </button>
            </div>
          </div>
        </PageScaffold>

        {/* Hidden print root */}
        <div style={{ position: "fixed", left: "-9999px", top: 0, opacity: 0 }} aria-hidden>
          <div id="ewill-print-root" ref={printRootRef} className="ew-doc">
            <EwillDocument
              includeCover={true}
              user={user}
              content={content}
              files={files}
              trusted={trusted}
              signatureName={signatureName}
              signatureDate={signatureDate}
              witness1Name={witness1Name}
              witness1Email={witness1Email}
              witness2Name={witness2Name}
              witness2Email={witness2Email}
              locationOriginal={locationOriginal}
              executorNotified={executorNotified}
              jurisdiction={jurisdiction}
              disclaimerText={disclaimerText}
              lastSaved={lastSaved}
            />
          </div>
        </div>

        <VideoLightbox
          open={showVideo}
          onClose={() => setShowVideo(false)}
          url="https://www.youtube.com/watch?v=7mhPeuNUn20"
          title="How the E-will page works"
          startAt={0}
          captions={false}
          startMuted={false}
        />

        {showDrawer && (
          <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
            <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
              <div className="dp-drawer-head">
                <div className="dp-drawer-title">
                  {drawerMode === "solicitor" ? "Find A Local Solicitor" : "How the E-will page works"}
                </div>
                <div className="dp-drawer-tools">
                  {drawerMode !== "solicitor" ? (
                  <button className="btn btn-main" onClick={() => downloadPDF()} type="button" data-allow-readonly>
                    Download
                  </button>
                ) : null}
                  <button className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button" data-allow-readonly>
                    Close
                  </button>
                </div>
              </div>
              <div className={"dp-drawer-body" + (drawerMode === "solicitor" ? " dp-drawer-body--directory" : "")}>
                {drawerMode === "solicitor" ? (
                  drawerBodyContent
                ) : (
                  <p>
                    As an Account Manager, you can <b>download</b> the full E-will as PDF, <b>export</b> a text summary, or <b>forward</b> details via email.
                  </p>
                )}
              </div>
            </aside>
          </div>
        )}

        <style>{`
          :root { --topbar-h:56px; --ink:#2a0516; --muted:#6b7280; --b:#ececf5; --b2:#d6d6ea; }
          .dp-help-row { display:flex; gap:10px; flex-wrap:wrap; }
          .dp-help { border-radius:8px; padding:10px 14px; display:inline-flex; align-items:center; gap:10px; font-weight:700; color:#fff; border:none; }
          .dp-help.read{ background:#650202; } .dp-help.see{ background:#003353; }
          .dp-intro{ margin:4px 0 10px; color:var(--ink); }

          .ew-solicitor-btn{ width:100%; background:#f97316; border-color:#f97316; display:inline-flex; align-items:center; justify-content:center; gap:10px; text-transform:none; }
          .ew-solicitor-btn__icon{ font-size:1.1rem; }
          .ew-drawer-iframe-wrap{ position:relative; display:flex; flex-direction:column; height:100%; min-height:0; gap:0; }
          .ew-drawer-copy{ color:var(--ink); font-weight:600; }
          .ew-drawer-iframe{ width:100%; flex:1; min-height:0; height:100%; border:0; border-radius:0; }
          .ew-drawer-iframe-footer{ flex:0 0 auto; display:flex; gap:10px; flex-wrap:wrap; justify-content:flex-end; background:#fff; border-top:1px solid #eee; padding:10px 12px; padding-bottom:max(12px, env(safe-area-inset-bottom)); }
          .ew-solicitor-link{ display:inline-flex; align-items:center; justify-content:center; gap:8px; text-decoration:none; }
          .ew-solicitor-return, .ew-solicitor-new-search{ flex:1 1 200px; gap:10px; display:inline-flex; align-items:center; justify-content:center; }
          .ew-solicitor-return__icon{ font-size:1.1rem; }

          .ew-directory-loading{ position:absolute; inset:0; display:grid; place-items:center; background:rgba(255,255,255,.75); backdrop-filter:saturate(120%) blur(1px); z-index:2; pointer-events:none; }
          .ew-directory-loading__text{ font-weight:900; color:#980000; letter-spacing:.2px; animation:ewPulse 1.2s ease-in-out infinite; }
          @keyframes ewPulse{ 0%,100%{ opacity:.35; transform:scale(.98);} 50%{ opacity:1; transform:scale(1);} }

          .dp-drawer{ z-index:2147483001; }
          .dp-drawer-body{ padding:16px; overflow:auto; flex:1; min-height:0; padding-bottom:max(16px, env(safe-area-inset-bottom)); }
          .dp-drawer-body--directory{ padding:0 !important; overflow:hidden !important; display:flex !important; flex-direction:column !important; min-height:0 !important; }

          @media (max-width:700px){
            .dp-drawer-bg{ align-items:flex-end; }
            .dp-drawer{ top:auto !important; height:75vh !important; bottom:calc(84px + env(safe-area-inset-bottom)) !important; }
          }

          .ew-card{ background:#fff; border:1px solid var(--b); border-radius:14px; padding:16px; }
          .ew-doc{ background:#fff; border:1px dashed var(--b); border-radius:12px; padding:12px; }
          .dp-drawer-bg{ position:fixed; inset:0; background:rgba(0,0,0,.3); z-index:2147483000; display:flex; justify-content:flex-end; }
          .dp-drawer{
            position:fixed; top:var(--topbar-h); right:0; height:calc(100vh - var(--topbar-h));
            width:min(540px,95vw); background:#fff; box-shadow:-8px 0 24px rgba(0,0,0,.15);
            border-top-left-radius:12px; border-bottom-left-radius:12px; display:flex; flex-direction:column; overflow:hidden;
            animation:dp-slide-in .22s ease-out both;
          }
          @media (min-width: 768px){
            .dp-drawer{ width:min(700px,90vw); }
          }
          @media (min-width: 1200px){
            .dp-drawer{ width:75vw; }
          }
          @keyframes dp-slide-in{ from{ transform:translateX(20px); opacity:0; } to{ transform:translateX(0); opacity:1; } }
          .dp-drawer-head{ display:flex; align-items:center; justify-content:space-between; gap:10px; padding:14px 16px; border-bottom:1px solid #f0f0f0; background:#fafafa; }
        `}</style>
      </div>
    );
  }

  // ==== OWNER VIEW ====
  const hasExisting = (content && content.trim().length > 0) || (files && files.length > 0);

  const ChangePrefButton = mode === "choose" ? null : (
    <div className="ew-prefbar">
      <button className="btn btn-danger" type="button" onClick={handleChangePreferences}>
        Change My Will Preferences
      </button>
    </div>
  );

  return (
    <div className="ew-root">
      {/* disable global FAB */}
      <PageScaffold
        title="E-will (Secure Electronic Will)"
        heroSrc="/lastwish-account-ewill.jpg"
        heroAlt="E-will"
        onAdd={undefined}
        afterHero={solicitorDrawerButton}
        actionsDesktop={
          <div className="dp-help-row">
            <button className="btn dp-help read" onClick={() => { setDrawerMode("how"); setShowDrawer(true); }}>
              <FiEye className="dp-help-ico" /> Read how it works
            </button>
            <button className="btn dp-help see" onClick={() => setShowVideo(true)}>
              <FaYoutube className="dp-help-ico" /> See how it works
            </button>
          </div>
        }
        actionsMobile={
          <div className="dp-help-row">
            <button className="btn dp-help read" onClick={() => { setDrawerMode("how"); setShowDrawer(true); }}>
              <FiEye className="dp-help-ico" /> Read how it works
            </button>
            <button className="btn dp-help see" onClick={() => setShowVideo(true)}>
              <FaYoutube className="dp-help-ico" /> See how it works
            </button>
          </div>
        }
      >
        {ChangePrefButton}

        {/* CHOOSE SCREEN */}
        {mode === "choose" && (
          <div className="ew-choose">
            <div className="ew-choose__grid">
              <button
                className="btn btn-main ew-choose__card"
                onClick={() => { setMode("create"); saveLocalPref("create"); }}
              >
                Create E-Will
              </button>
              <button
                className="btn btn-secondary ew-choose__card"
                onClick={() => { setMode("upload"); saveLocalPref("upload"); }}
              >
                <FiUploadCloud style={{ marginRight: 8 }} /> Upload Will
              </button>
            </div>
            {(hasExisting || persistedPref) && (
              <div className="ew-choose__hint">
                You already have saved data.{" "}
                <button
                  className="linkbtn"
                  type="button"
                  onClick={() => setMode((persistedPref === "upload" ? "upload" : "create"))}
                >
                  Continue where you left off ({persistedPref || "create"})
                </button>
              </div>
            )}
          </div>
        )}

        {/* CREATE MODE */}
        {mode === "create" && (
          <div className="ew-card">
            {/* Editor */}
            <div className="ew-quill">
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                placeholder="Start writing your will here…"
                modules={quillModules}
              />
            </div>

            {/* Attachments */}
            <div className="ew-section">
              <h3>Supporting Documents</h3>
              <div className="ew-upload-row">
                <input type="file" ref={fileInput} onChange={handleFileInputMulti} multiple disabled={fileUploading} />
                {fileUploading && <span className="ew-uploading">Uploading…</span>}
              </div>
              <ul className="ew-files">
                {files.map((f) => (
                  <li key={f.url} className="ew-file">
                    <a href={f.url} target="_blank" rel="noopener noreferrer">{f.name}</a>
                    <button className="btn btn-danger" type="button" onClick={() => handleDeleteFile(f)}>Delete</button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Trusted */}
            <div className="ew-section">
              <h3>Trusted Contacts / Executors</h3>
              <div className="ew-trusted-input">
                <input
                  type="email"
                  className="input"
                  placeholder="Add trusted contact email"
                  value={newTrusted}
                  onChange={(e) => setNewTrusted(e.target.value)}
                />
                <button className="btn btn-main" type="button" onClick={() => {
                  const v = (newTrusted || "").trim();
                  if (!v || trusted.includes(v)) return;
                  setTrusted([...trusted, v]);
                  setNewTrusted("");
                }}>Add</button>
              </div>
              <div className="ew-trusted-list">
                {trusted.map((email) => (
                  <span key={email} className="ew-trusted-pill">
                    {email}
                    <button className="btn btn-cancel" type="button" onClick={() => setTrusted((arr) => arr.filter((x) => x !== email))}>×</button>
                  </span>
                ))}
              </div>
            </div>

            {/* Settings + Signing */}
            <div className="ew-grid">
              <div className="ew-section">
                <h3>Settings</h3>
                <label className="label">Jurisdiction</label>
                <select className="input" value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)}>
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>

                <label className="label" style={{ marginTop: 10 }}>Location of Original Paper Will</label>
                <input
                  className="input"
                  value={locationOriginal}
                  onChange={(e) => setLocationOriginal(e.target.value)}
                  placeholder="e.g., In safe at home, with solicitor…"
                />

                <label className="ck-row" style={{ marginTop: 10 }}>
                  <input type="checkbox" checked={executorNotified} onChange={(e) => setExecutorNotified(e.target.checked)} />
                  I confirm my executors have been informed.
                </label>

                <div className="ew-disclaimer">
                  <b>Note:</b> {disclaimerText}
                </div>
              </div>

              {/* Signing */}
              <div className="ew-section">
                <h3>Signing</h3>
                <label className="label">Your full name (signature)</label>
                <input className="input" value={signatureName} onChange={(e) => setSignatureName(e.target.value)} placeholder="Type your full name" />

                <label className="label" style={{ marginTop: 10 }}>Date of signing</label>
                <input className="input" type="date" value={signatureDate} onChange={(e) => setSignatureDate(e.target.value)} />

                <div className="grid2" style={{ marginTop: 10 }}>
                  <div>
                    <label className="label">Witness 1 — Name</label>
                    <input className="input" value={witness1Name} onChange={(e) => setWitness1Name(e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Witness 1 — Email</label>
                    <input className="input" type="email" value={witness1Email} onChange={(e) => setWitness1Email(e.target.value)} />
                  </div>
                </div>
                <div className="grid2">
                  <div>
                    <label className="label">Witness 2 — Name</label>
                    <input className="input" value={witness2Name} onChange={(e) => setWitness2Name(e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Witness 2 — Email</label>
                    <input className="input" type="email" value={witness2Email} onChange={(e) => setWitness2Email(e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            {/* Actions (owner) */}
            <div className="ew-toolbar">
              <button className="btn btn-main" onClick={async () => { await handleSave(); }}> {saving ? "Saving…" : "Save E-will"} </button>
              <button className="btn btn-secondary" type="button" onClick={() => setPreviewOpen(true)}>Preview Full Document</button>
              <button className="btn" type="button" onClick={() => { const root = printRootRef.current; if (root) printFull(); }}>Print / Save PDF (Full)</button>
              <button className="btn" type="button" onClick={downloadPDF}>Download PDF (Full)</button>
              <label className="ck-row" style={{ marginLeft: "auto" }}>
                <input type="checkbox" checked={includeCover} onChange={(e) => setIncludeCover(e.target.checked)} /> Include cover page
              </label>
              {lastSaved && <div className="ew-saved">Last updated: {lastSaved.toLocaleString()}</div>}
            </div>
          </div>
        )}

        {/* UPLOAD MODE */}
        {mode === "upload" && (
          <div className="ew-card">
            <div
              className="ew-dropzone"
              onDrop={onDrop}
              onDragOver={onDragOver}
              role="button"
              aria-label="Drag and drop will files here"
            >
              <FiUploadCloud className="ew-dz-ico" />
              <div className="ew-dz-title">Drop your will files here</div>
              <div className="ew-dz-sub">PDF, DOCX, images. You can add multiple files.</div>
              <div className="ew-dz-actions">
                <label className="btn btn-secondary">
                  Browse files
                  <input type="file" multiple onChange={handleFileInputMulti} style={{ display: "none" }} />
                </label>
              </div>
              {fileUploading && <div className="ew-uploading">Uploading…</div>}
            </div>

            {!!files.length && (
              <ul className="ew-files" style={{ marginTop: 12 }}>
                {files.map((f) => (
                  <li key={f.url} className="ew-file">
                    <a href={f.url} target="_blank" rel="noopener noreferrer">{f.name}</a>
                    <button className="btn btn-danger" type="button" onClick={() => handleDeleteFile(f)}>Remove</button>
                  </li>
                ))}
              </ul>
            )}

            <div className="ew-section" style={{ marginTop: 16 }}>
              <h3>Signatory & Date</h3>
              <div className="grid2">
                <div>
                  <label className="label">Your full name (signature)</label>
                  <input className="input" value={signatureName} onChange={(e) => setSignatureName(e.target.value)} placeholder="Type your full name" />
                </div>
                <div>
                  <label className="label">Date of signing</label>
                  <input className="input" type="date" value={signatureDate} onChange={(e) => setSignatureDate(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="ew-toolbar" style={{ marginTop: 12 }}>
              <button className="btn" type="button" onClick={() => setMode("choose")}>Back</button>
              <button className="btn btn-main" type="button" onClick={async () => { await handleSave(); }} disabled={saving || files.length === 0}>
                {saving ? "Saving…" : "Save Uploaded Will"}
              </button>
            </div>
          </div>
        )}
      </PageScaffold>

      {/* “Saved” toast */}
      {justSaved && (
        <div className="ew-toast">Congratulations — your changes have been saved.</div>
      )}

      {/* Hidden print root */}
      <div style={{ position: "fixed", left: "-9999px", top: 0, opacity: 0 }} aria-hidden>
        <div id="ewill-print-root" ref={printRootRef} className="ew-doc">
          <EwillDocument
            includeCover={includeCover}
            user={user}
            content={content}
            files={files}
            trusted={trusted}
            signatureName={signatureName}
            signatureDate={signatureDate}
            witness1Name={witness1Name}
            witness1Email={witness1Email}
            witness2Name={witness2Name}
            witness2Email={witness2Email}
            locationOriginal={locationOriginal}
            executorNotified={executorNotified}
            jurisdiction={jurisdiction}
            disclaimerText={disclaimerText}
            lastSaved={lastSaved}
          />
        </div>
      </div>

      {/* Preview modal */}
      {previewOpen && (
        <div className="modal-bg" onClick={() => setPreviewOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820 }}>
            <h3>Your E-will (Preview)</h3>
            <div className="modal-body">
              <div id="ewill-preview-print" className="ew-doc">
                <EwillDocument
                  includeCover={includeCover}
                  user={user}
                  content={content}
                  files={files}
                  trusted={trusted}
                  signatureName={signatureName}
                  signatureDate={signatureDate}
                  witness1Name={witness1Name}
                  witness1Email={witness1Email}
                  witness2Name={witness2Name}
                  witness2Email={witness2Email}
                  locationOriginal={locationOriginal}
                  executorNotified={executorNotified}
                  jurisdiction={jurisdiction}
                  disclaimerText={disclaimerText}
                  lastSaved={lastSaved}
                />
              </div>
              <div className="form-actions">
                <button className="btn" type="button" onClick={() => { const root = printRootRef.current; if (root) printFull(); }}>Print / Save PDF</button>
                <button className="btn" type="button" onClick={downloadPDF}>Download PDF</button>
                <button className="btn btn-cancel" type="button" onClick={() => setPreviewOpen(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View version modal */}
      {viewVersion && (
        <div className="modal-bg" onClick={() => setViewVersion(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820 }}>
            <h3>Version from {new Date(viewVersion.updatedISO).toLocaleString()}</h3>
            <div className="modal-body">
              <div className="ew-doc">
                <EwillDocument
                  includeCover={includeCover}
                  user={user}
                  {...(viewVersion.snapshot || { content: viewVersion.content || "" })}
                  disclaimerText={disclaimerText}
                  lastSaved={new Date(viewVersion.updatedISO)}
                />
              </div>
              <div className="form-actions">
                <button className="btn btn-cancel" type="button" onClick={() => setViewVersion(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://www.youtube.com/watch?v=7mhPeuNUn20"
        title="How the E-will page works"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">
                {drawerMode === "solicitor" ? "Find A Local Solicitor" : "How the E-will page works"}
              </div>
              <div className="dp-drawer-tools">
                {drawerMode !== "solicitor" && mode !== "choose" ? (
                  <button className="btn btn-main" onClick={() => setPreviewOpen(true)} type="button">
                    Preview
                  </button>
                ) : null}
                <button className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button">
                  Close
                </button>
              </div>
            </div>
            <div className={"dp-drawer-body" + (drawerMode === "solicitor" ? " dp-drawer-body--directory" : "")}>
              {drawerMode === "solicitor" ? (
                drawerBodyContent
              ) : (
                <p>
                  Choose <b>Create E-Will</b> to type your will within the app, or <b>Upload Will</b> to securely store your existing signed will. You can add witnesses and metadata in both paths.
                </p>
              )}
            </div>
          </aside>
        </div>
      )}

      {/* Scoped styles for owner additions */}
      <style>{`
        :root { --topbar-h:56px; --ink:#2a0516; --muted:#6b7280; --b:#ececf5; --b2:#d6d6ea; }
        .dp-help-row { display:flex; gap:10px; flex-wrap:wrap; }
        .dp-help { border-radius:8px; padding:10px 14px; display:inline-flex; align-items:center; gap:10px; font-weight:700; color:#fff; border:none; }
        .dp-help.read{ background:#650202; } .dp-help.see{ background:#003353; }
        .dp-help-ico{ font-size:1.05rem; }
        .dp-intro{ margin:4px 0 10px; color:var(--ink); }
        .ew-solicitor-btn{ width:100%; background:#f97316; border-color:#f97316; display:inline-flex; align-items:center; justify-content:center; gap:10px; text-transform:none; }
        .ew-solicitor-btn__icon{ font-size:1.1rem; }
        .ew-drawer-iframe-wrap{ position:relative; display:flex; flex-direction:column; height:100%; min-height:0; gap:0; }
        .ew-drawer-copy{ color:var(--ink); font-weight:600; }
        .ew-drawer-iframe{ width:100%; flex:1; min-height:0; height:100%; border:0; border-radius:0; }
        .ew-drawer-iframe-footer{ flex:0 0 auto; display:flex; gap:10px; flex-wrap:wrap; justify-content:flex-end; background:#fff; border-top:1px solid #eee; padding:10px 12px; padding-bottom:max(12px, env(safe-area-inset-bottom)); }
        .ew-solicitor-link{ display:inline-flex; align-items:center; justify-content:center; gap:8px; text-decoration:none; }
        .ew-solicitor-return, .ew-solicitor-new-search{ flex:1 1 200px; gap:10px; display:inline-flex; align-items:center; justify-content:center; }
        .ew-solicitor-return__icon{ font-size:1.1rem; }

          .ew-directory-loading{ position:absolute; inset:0; display:grid; place-items:center; background:rgba(255,255,255,.75); backdrop-filter:saturate(120%) blur(1px); z-index:2; pointer-events:none; }
          .ew-directory-loading__text{ font-weight:900; color:#980000; letter-spacing:.2px; animation:ewPulse 1.2s ease-in-out infinite; }
          @keyframes ewPulse{ 0%,100%{ opacity:.35; transform:scale(.98);} 50%{ opacity:1; transform:scale(1);} }

          .dp-drawer{ z-index:2147483001; }
          .dp-drawer-body{ padding:16px; overflow:auto; flex:1; min-height:0; padding-bottom:max(16px, env(safe-area-inset-bottom)); }
          .dp-drawer-body--directory{ padding:0 !important; overflow:hidden !important; display:flex !important; flex-direction:column !important; min-height:0 !important; }

          @media (max-width:700px){
            .dp-drawer-bg{ align-items:flex-end; }
            .dp-drawer{ top:auto !important; height:75vh !important; bottom:calc(84px + env(safe-area-inset-bottom)) !important; }
          }

        .ew-prefbar{ display:flex; justify-content:flex-end; margin-bottom:10px; }

        .ew-choose { background:#fff; border:1px solid var(--b); border-radius:14px; padding:18px; }
        .ew-choose__grid { display:grid; grid-template-columns:1fr; gap:12px; }
        @media(min-width:700px){ .ew-choose__grid { grid-template-columns:1fr 1fr; } }
        .ew-choose__card { padding:18px 20px; border-radius:12px; font-weight:700; justify-content:center; }
        .ew-choose__hint { margin-top:12px; color:#4b5563; }
        .linkbtn { background:none; border:none; padding:0; color:#003353; text-decoration:underline; cursor:pointer; }

        .ew-card{ background:#fff; border:1px solid var(--b); border-radius:14px; padding:16px; }
        .ew-section{ background:#fff; border:1px solid var(--b); border-radius:12px; padding:12px; margin-top:14px; }

        .ew-dropzone{
          border:2px dashed var(--b2); border-radius:14px; padding:28px; text-align:center; background:#fafafa;
        }
        .ew-dz-ico{ font-size:36px; opacity:.9; }
        .ew-dz-title{ font-weight:800; color:#0f172a; margin-top:8px; }
        .ew-dz-sub{ color:#6b7280; margin-top:4px; }
        .ew-dz-actions{ margin-top:10px; }
        .ew-uploading{ margin-top:10px; color:#6b7280; }

        .ew-quill .ql-toolbar{ border-radius:12px 12px 0 0; }
        .ew-quill .ql-container{ border-radius:0 0 12px 12px; min-height:220px; }
        .ew-quill .ql-editor{ min-height:200px; }

        .label{ display:block; font-size:13px; color:#6b7280; font-weight:600; margin:6px 0 4px; }
        .input{
          width:100%; min-height:42px; padding:10px 12px; border:1px solid var(--b2); border-radius:10px; background:#fff; color:var(--ink);
          outline:none; transition:border-color .15s, box-shadow .15s;
        }
        .input:focus{ border-color:#2a0516; box-shadow:0 0 0 3px rgba(42,5,22,.12); }

        .ck-row{ display:flex; gap:8px; align-items:center; color:var(--ink); }

        .grid2{ display:grid; grid-template-columns:1fr; gap:10px; }
        @media(min-width:700px){ .grid2{ grid-template-columns:1fr 1fr; } }

        .ew-grid{ display:grid; grid-template-columns:1fr; gap:16px; }
        @media(min-width:900px){ .ew-grid{ grid-template-columns:1fr 1fr; } }

        .ew-trusted-input{ display:flex; gap:8px; flex-wrap:wrap; }
        .ew-trusted-input .input{ flex:1 1 260px; }
        .ew-trusted-list{ display:flex; gap:8px; flex-wrap:wrap; margin-top:10px; }
        .ew-trusted-pill{
          display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border-radius:999px;
          background:#f3f4f6; border:1px solid #e5e7eb; color:var(--ink);
        }

        .ew-upload-row{ display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
        .ew-files{ list-style:none; padding:0; margin:10px 0 0; }
        .ew-file{ display:flex; align-items:center; justify-content:space-between; gap:10px; padding:8px 10px; border:1px solid var(--b); border-radius:10px; background:#fafafa; }

        .ew-versions{ list-style:none; padding:0; margin:10px 0 0; display:grid; gap:8px; }
        .ew-version-row{ display:flex; align-items:center; justify-content:space-between; gap:10px; border:1px solid var(--b); border-radius:10px; padding:8px 10px; background:#fff; }
        .ew-version-meta{ color:#555; }
        .ew-version-actions{ display:flex; gap:8px; }

        .ew-toolbar{ display:flex; gap:8px; flex-wrap:wrap; align-items:center; margin-top:12px; }
        .ew-saved{ color:#657899; font-size:12px; }

        .ew-toast{
          position: fixed;
          right: 16px;
          bottom: 16px;
          background: #f97316; /* orange */
          color: #fff;
          padding: 10px 14px;
          border-radius: 10px;
          box-shadow: 0 6px 16px rgba(0,0,0,.18);
          z-index: 9999;
          font-weight: 700;
        }

        .dp-drawer-bg{ position:fixed; inset:0; background:rgba(0,0,0,.3); z-index:2147483000; display:flex; justify-content:flex-end; }
        .dp-drawer{
          position:fixed; top:var(--topbar-h); right:0; height:calc(100vh - var(--topbar-h));
          width:min(520px,95vw); background:#fff; box-shadow:-8px 0 24px rgba(0,0,0,.15);
          border-top-left-radius:12px; border-bottom-left-radius:12px; display:flex; flex-direction:column; overflow:hidden;
          animation:dp-slide-in .22s ease-out both;
        }
        @media(min-width:1200px){
          .dp-drawer{ width:75vw; }
        }
        @keyframes dp-slide-in{ from{ transform:translateX(20px); opacity:0; } to{ transform:translateX(0); opacity:1; } }
        .dp-drawer-head{ display:flex; align-items:center; justify-content:space-between; gap:10px; padding:14px 16px; border-bottom:1px solid #f0f0f0; background:#fafafa; }
        .dp-drawer-title{ font-weight:700; color:#2a0516; }
        .dp-drawer-tools{ display:flex; gap:8px; }
        .dp-drawer-body{ padding:16px; overflow:auto; flex:1; min-height:0; padding-bottom:max(16px, env(safe-area-inset-bottom)); }

        /* Document + cover */
        .ew-doc__container{ font-family:"Inter", "Helvetica Neue", Arial, sans-serif; color:#0f172a; background:#fff; }
        .ew-cover{ text-align:center; padding:32px 24px; border:1px solid var(--b); border-radius:12px; background:#fff; }
        .ew-cover__logo{ width:150px; height:auto; max-height:150px; object-fit:contain; display:block; margin:0 auto 12px; }
        .ew-cover__title{ font-size:32px; margin:4px 0 6px; color:#111827; }
        .ew-cover__meta{ display:grid; gap:4px; justify-content:center; color:#374151; }
        .ew-doc__wrap{ border:1px solid var(--b); border-radius:12px; padding:20px; background:#fff; }
        .ew-doc__header{ display:flex; flex-direction:column; gap:4px; margin-bottom:10px; }
        .ew-doc__title{ margin:0; font-size:26px; color:#111827; }
        .ew-doc__meta{ color:#6b7280; font-size:14px; }
        .ew-doc__section{ margin-top:14px; }
        .ew-doc__section h2{ margin:0 0 6px; font-size:18px; color:#111827; }
        .ew-doc__list{ padding-left:18px; margin:6px 0 0; }
        .ew-doc__pilllist{ list-style:none; padding:0; display:flex; flex-wrap:wrap; gap:8px; margin:6px 0 0; }
        .ew-doc__pilllist li{ padding:6px 10px; border-radius:999px; background:#f3f4f6; border:1px solid #e5e7eb; }
        .ew-doc__muted{ color:#6b7280; }
        .ew-doc__def{ display:grid; gap:4px; color:#111827; }
        .ew-doc__note{ margin-top:8px; color:#6b7280; font-size:14px; }
        .ew-doc__signs{ display:grid; grid-template-columns:1fr; gap:10px; margin-top:6px; }
        @media(min-width:640px){ .ew-doc__signs{ grid-template-columns:1fr 1fr; } }
        .ew-doc__sigline{ min-height:36px; border-bottom:2px solid #111827; padding-bottom:6px; }
        .ew-doc__sigcap{ margin-top:4px; color:#6b7280; font-size:14px; }
        .ew-doc__witnesses{ display:grid; grid-template-columns:1fr; gap:12px; margin-top:10px; }
        @media(min-width:640px){ .ew-doc__witnesses{ grid-template-columns:1fr 1fr; } }
      `}</style>
    </div>
  );
}

// ---------- Full Document (cover + body) ----------
function EwillDocument(props) {
  const { includeCover, user, lastSaved, disclaimerText, ...rest } = props;
  const owner = user?.displayName || user?.email || user?.uid || "Owner";
  return (
    <div className="ew-doc__container">
      {includeCover && (
        <section className="ew-cover">
          <img src={COVER_LOGO_SRC} alt="Logo" className="ew-cover__logo" onError={(e) => (e.currentTarget.style.display = 'none')} />
          <h1 className="ew-cover__title">Electronic Will</h1>
          <div className="ew-cover__meta">
            <div><b>Owner:</b> {owner}</div>
            <div><b>Generated:</b> {new Date().toLocaleString()}</div>
            {lastSaved ? <div><b>Last updated:</b> {lastSaved.toLocaleString()}</div> : null}
          </div>
        </section>
      )}
      {includeCover && <div className="page-break" />}
      <EwillPreview lastSaved={lastSaved} disclaimerText={disclaimerText} {...rest} />
    </div>
  );
}

function EwillPreview({
  content,
  files,
  trusted,
  signatureName,
  signatureDate,
  witness1Name,
  witness1Email,
  witness2Name,
  witness2Email,
  locationOriginal,
  executorNotified,
  jurisdiction,
  disclaimerText,
  lastSaved,
}) {
  const safeContent = useMemo(() => {
    const fallback = "<p><em>(empty)</em></p>";
    return sanitizeRichText(content || fallback);
  }, [content]);

  return (
    <div className="ew-doc__wrap">
      <header className="ew-doc__header">
        <h1 className="ew-doc__title">Electronic Will</h1>
        {lastSaved ? <div className="ew-doc__meta">Last updated: {lastSaved.toLocaleString()}</div> : null}
      </header>

      <section className="ew-doc__section">
        <h2>Will Text</h2>
        <div className="ql-snow">
          <div className="ql-editor" dangerouslySetInnerHTML={{ __html: safeContent }} />
        </div>
      </section>

      <section className="ew-doc__section">
        <h2>Supporting Documents</h2>
        {files?.length ? (
          <ul className="ew-doc__list">
            {files.map((f) => (
              <li key={f.url}><a href={f.url}>{f.name}</a></li>
            ))}
          </ul>
        ) : (
          <div className="ew-doc__muted">No attachments</div>
        )}
      </section>

      <section className="ew-doc__section">
        <h2>Trusted Contacts / Executors</h2>
        {trusted?.length ? (
          <ul className="ew-doc__pilllist">
            {trusted.map((e) => (<li key={e}>{e}</li>))}
          </ul>
        ) : (<div className="ew-doc__muted">None listed</div>)}
      </section>

      <section className="ew-doc__section">
        <h2>Settings</h2>
        <div className="ew-doc__def">
          <div><b>Jurisdiction:</b> {jurisdiction}</div>
          <div><b>Location of Original Will:</b> {locationOriginal || "—"}</div>
          <div><b>Executors Informed:</b> {executorNotified ? "Yes" : "No"}</div>
        </div>
        <div className="ew-doc__note"><b>Disclaimer:</b> {disclaimerText}</div>
      </section>

      <section className="ew-doc__section">
        <h2>Signatures</h2>
        <div className="ew-doc__signs">
          <div className="ew-doc__sig">
            <div className="ew-doc__sigline">{signatureName || ""}</div>
            <div className="ew-doc__sigcap">Testator’s Signature</div>
          </div>
          <div className="ew-doc__sig">
            <div className="ew-doc__sigline">{signatureDate || ""}</div>
            <div className="ew-doc__sigcap">Date</div>
          </div>
        </div>
        <div className="ew-doc__witnesses">
          <div className="ew-doc__wit">
            <div className="ew-doc__sigline">{witness1Name || ""}</div>
            <div className="ew-doc__sigcap">Witness 1 — Name</div>
            <div className="ew-doc__muted">{witness1Email || ""}</div>
          </div>
          <div className="ew-doc__wit">
            <div className="ew-doc__sigline">{witness2Name || ""}</div>
            <div className="ew-doc__sigcap">Witness 2 — Name</div>
            <div className="ew-doc__muted">{witness2Email || ""}</div>
          </div>
        </div>
      </section>
    </div>
  );
}
